import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Paper,
  Divider,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccessTime,
  Person,
  Event,
  Delete,
  Edit,
  UpcomingOutlined,
  History,
  CalendarMonth,
  ContentCopy,
  Add,
  FileDownloadOutlined,
  Search,
  Tune,
  ViewList,
  ViewWeek,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format, startOfDay } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import CopyDayDialog from '../components/CopyDayDialog';
import SessionFormDialog from '../components/SessionFormDialog';
import DeleteSessionDialog from '../components/DeleteSessionDialog';
import CreateSessionsDialog from '../components/CreateSessionsDialog';
import BulkSessionWorkspace from '../components/BulkSessionWorkspace';
import BranchSelect from '../components/BranchSelect';
import { useMyLocations } from '../hooks/useMyLocations';
import ImportSessionsDialog from '../components/multiLocation/ImportSessionsDialog';
import { useMultiLocationAvailable } from '../components/multiLocation/shared';
import ScheduleCalendarView from '../components/schedule/ScheduleCalendarView';
import { SCHED, btnPrimarySx, btnSx, segBtnSx, segSx } from '../components/schedule/scheduleTheme';

interface Session {
  _id: string;
  className: string;
  date: string;
  slots: number;
  availableSlots?: number;
  price: number;
  duration: number;
  image?: string;
  class?: { _id: string };
  instructor?: { _id: string; name: string; image?: string };
  classType?: { _id: string; title: string; name?: string; studioType?: 'workout' | 'wellness' | 'event' };
  sessionType?: string;
  // Derived server-side from the ClassType. 'workout' (default) | 'wellness'.
  category?: 'workout' | 'wellness';
  studio?: { _id: string } | string;
}

const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * IST date helpers — MODULE SCOPE on purpose.
 *
 * They close over no props or state, so defining them inside the component gave
 * them a new identity on every render, and react-hooks/exhaustive-deps then
 * demanded them in four dependency arrays. Adding them there would have been
 * worse than the warning: every callback and effect would re-fire on each
 * render. Hoisting makes the reference stable and the warnings disappear.
 *
 * `getTodayISTDateKey` stays a FUNCTION rather than a computed constant — it
 * reads the clock at call time, so a tab left open overnight still rolls to the
 * new day.
 */
const istDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: IST_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
/** 'yyyy-MM-dd' in IST — the cache key for both month and day buckets. */
const toISTDateKey = (d: Date): string => istDateFormatter.format(d);
const getTodayISTDateKey = () => toISTDateKey(new Date());

const StudioSessionsPage: React.FC = () => {
  // `canWriteSessions` is false only for an instructor whose studio turned
  // `canManageSessions` off. Every schedule-writing control on this page hangs
  // off it: without this the panel drew Create / Bulk Create / Bulk Edit /
  // Edit / Delete for anyone who could open the page, and the backend's
  // StudioSessionWriteGuard was the first and only thing to say no.
  const { admin, canWriteSessions } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const pageView: 'list' | 'calendar' =
    // Calendar is always the default; List only while the URL asks for it.
    viewParam === 'list' ? 'list' : 'calendar';
  const handlePageViewChange = (_e: React.MouseEvent<HTMLElement>, next: 'list' | 'calendar' | null) => {
    if (!next) return;
    setSearchParams(next === 'list' ? { view: 'list' } : {}, { replace: true });
  };
  // Bumped after any create / edit / delete / copy so the calendar view refetches too.
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const accountStudioId = (admin as any)?.studio?._id || '';

  // ── WHICH BRANCH AM I WRITING TO ────────────────────────────────────────
  //
  // The calendar aggregates every location, which is the point. But a session
  // points at a CLASS and an INSTRUCTOR, and those are per-branch documents —
  // Koramangala's "Reformer 50" is a different record from Yelahanka's. So a
  // write has to name one branch before the dialogs can even load the right
  // dropdowns; a session created against another branch's class list would be
  // taught by someone who does not work there.
  //
  // Starts empty on purpose. Pre-selecting a branch is the same silent
  // default that put sessions at the wrong address in the first place.
  const { isAllLocations } = useMyLocations();
  // The location picked for the bulk action being opened ("Which location?"
  // below). It scopes that dialog only — the page's own reads keep `studioId`,
  // so picking a location for a bulk action never changes what the calendar
  // shows.
  const [writeBranch, setWriteBranch] = useState('');
  const studioId = accountStudioId;

  // Two or more locations this account manages: Create Session picks its
  // locations itself (so it needs no "Create at" first), and Import appears.
  // A single-location studio keeps today's dialogs unchanged.
  const multiLocation = useMultiLocationAvailable(accountStudioId);
  const [importSessionsOpen, setImportSessionsOpen] = useState(false);
  const currentLocation = isAllLocations ? (writeBranch ? [writeBranch] : []) : accountStudioId ? [accountStudioId] : [];

  // ── TWO CONTROLS: "Create Sessions" AND A "More" MENU ─────────────────────
  //
  // Creating is ONE form (CreateSessionsDialog): one session, a repeating
  // range, at one location or several — it replaced Create Session, Bulk
  // Create and "At several locations". Everything else that changes many
  // sessions (Copy Day, Bulk Edit / Delete, Import) sits in the More menu. A
  // brand with two or more locations is asked "Which location?" before Copy Day
  // or Bulk Edit / Delete — even when one location is selected — so a bulk
  // change is never made somewhere the operator did not consciously choose. A
  // single-location studio opens them straight away, exactly as before.
  type BulkAction = 'copyDay' | 'bulkEdit';
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<HTMLElement | null>(null);
  const [locationAsk, setLocationAsk] = useState<BulkAction | null>(null);
  const [askedLocation, setAskedLocation] = useState('');
  const openAction = (action: BulkAction, at: string) => {
    setWriteBranch(at);
    if (action === 'copyDay') setCopyDayDialogOpen(true);
    else setBulkWorkspaceOpen(true);
  };
  const startAction = (action: BulkAction) => {
    setBulkMenuAnchor(null);
    if (multiLocation || isAllLocations) {
      setAskedLocation(isAllLocations ? '' : accountStudioId);
      setLocationAsk(action);
    } else {
      openAction(action, '');
    }
  };
  // The location the open dialog works at: the one just picked, else the
  // page's own studio.
  const actionStudioId = writeBranch || studioId;
  const openCreateSession = () => setCreateSessionDialogOpen(true);
  // Wellness controls appear ONLY for studios that offer wellness. Workout-only
  // studios (Purple Pilates, HoP, P&P, Laya) never see the toggle — flow unchanged.
  const studioDoc = (admin as any)?.studio;
  const offersWellness =
    studioDoc?.studioPermissions?.enableWellness === true ||
    studioDoc?.studioType === 'wellness' ||
    (Array.isArray(studioDoc?.studioTypes) && studioDoc.studioTypes.includes('wellness'));

  const getCurrentISTDate = () => startOfDay(toZonedTime(new Date(), IST_TIMEZONE));


  const [selectedDate, setSelectedDate] = useState<Date>(getCurrentISTDate());
  const [sessionSearchTerm, setSessionSearchTerm] = useState('');
  // 'workout' (default — unchanged behaviour) | 'wellness'
  const [categoryFilter, setCategoryFilter] = useState<'workout' | 'wellness'>('workout');
  // Fast path: today's sessions, fetched immediately so the page isn't blocked on the full month.
  const [daySessions, setDaySessions] = useState<Session[]>([]);
  // Month cache: 'YYYY-MM' -> sessions. For the current month this only ever contains
  // today-onward (past days in the current month are deliberately never bulk-fetched);
  // for a fully past month it stays []; for a future month it holds the whole month.
  const [monthSessions, setMonthSessions] = useState<Record<string, Session[]>>({});
  // Past-day cache: 'YYYY-MM-DD' -> sessions, populated lazily only when that specific
  // past date is actually viewed (clicked), never as part of a month bulk load.
  const [pastDaySessions, setPastDaySessions] = useState<Record<string, Session[]>>({});
  const [isLoadingDaySessions, setIsLoadingDaySessions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Month currently shown in the calendar widget; can differ from selectedDate's month
  // when the user pages the calendar with the arrows without picking a day.
  const [displayedMonthStr, setDisplayedMonthStr] = useState<string>(() => getTodayISTDateKey().slice(0, 7));
  const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);
  const [copyDayDialogOpen, setCopyDayDialogOpen] = useState(false);
  const [createSessionDialogOpen, setCreateSessionDialogOpen] = useState(false);
  const [editSessionDialogOpen, setEditSessionDialogOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<Session | null>(null);
  const [bulkWorkspaceOpen, setBulkWorkspaceOpen] = useState(false);

  const daySessionsFetchIdRef = useRef(0);
  const monthSessionsFetchIdRef = useRef<Record<string, number>>({});
  const loadedMonthsRef = useRef<Set<string>>(new Set());
  const pastDayFetchIdRef = useRef<Record<string, number>>({});
  const loadedDaysRef = useRef<Set<string>>(new Set());

  const fetchSessionsForMonth = useCallback(async (monthStr: string) => {
    if (!studioId) return;
    loadedMonthsRef.current.add(monthStr);
    const fetchId = (monthSessionsFetchIdRef.current[monthStr] ?? 0) + 1;
    monthSessionsFetchIdRef.current[monthStr] = fetchId;

    const todayStr = getTodayISTDateKey();
    const [year, month] = monthStr.split('-');
    const monthStart = `${year}-${month}-01`;
    const lastDay = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
    const monthEnd = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
    // Never bulk-fetch days that are already in the past — clamp the range start to
    // today; if the whole month is in the past, skip the network call entirely.
    const startDate = monthStart < todayStr ? (todayStr <= monthEnd ? todayStr : null) : monthStart;

    if (startDate === null) {
      setMonthSessions((prev) => ({ ...prev, [monthStr]: [] }));
      return;
    }

    try {
      const response = await apiService.getStudioSessions(studioId, {
        startDate,
        endDate: monthEnd,
        ...(categoryFilter === 'wellness' ? { category: 'wellness' } : {}),
      });
      if (fetchId !== monthSessionsFetchIdRef.current[monthStr]) return;
      setMonthSessions((prev) => ({ ...prev, [monthStr]: response.sessions || [] }));
    } catch (err: any) {
      if (fetchId !== monthSessionsFetchIdRef.current[monthStr]) return;
      console.error('Error fetching sessions for month:', monthStr, err);
      setError(err.message || 'Failed to fetch sessions');
      loadedMonthsRef.current.delete(monthStr);
    }
  }, [studioId, categoryFilter]);

  const fetchSessionsForPastDate = useCallback(async (dateStr: string) => {
    if (!studioId) return;
    loadedDaysRef.current.add(dateStr);
    const fetchId = (pastDayFetchIdRef.current[dateStr] ?? 0) + 1;
    pastDayFetchIdRef.current[dateStr] = fetchId;
    try {
      const response = await apiService.getStudioSessions(studioId, {
        date: dateStr,
        ...(categoryFilter === 'wellness' ? { category: 'wellness' } : {}),
      });
      if (fetchId !== pastDayFetchIdRef.current[dateStr]) return;
      setPastDaySessions((prev) => ({ ...prev, [dateStr]: response.sessions || [] }));
    } catch (err: any) {
      if (fetchId !== pastDayFetchIdRef.current[dateStr]) return;
      console.error('Error fetching sessions for past date:', dateStr, err);
      setError(err.message || 'Failed to fetch sessions');
      loadedDaysRef.current.delete(dateStr);
    }
  }, [studioId, categoryFilter]);

  // Today-first fast path: fetch just today's sessions so the page renders instantly,
  // then kick off the (clamped) month load in the background if it isn't cached yet.
  const fetchTodaySessions = useCallback(async () => {
    if (!studioId) return;
    const todayStr = getTodayISTDateKey();
    const fetchId = ++daySessionsFetchIdRef.current;
    setIsLoadingDaySessions(true);
    setError(null);
    try {
      const response = await apiService.getStudioSessions(studioId, {
        date: todayStr,
        ...(categoryFilter === 'wellness' ? { category: 'wellness' } : {}),
      });
      if (fetchId !== daySessionsFetchIdRef.current) return;
      setDaySessions(response.sessions || []);

      const monthStr = todayStr.slice(0, 7);
      if (!loadedMonthsRef.current.has(monthStr)) {
        void fetchSessionsForMonth(monthStr);
      }
    } catch (err: any) {
      if (fetchId !== daySessionsFetchIdRef.current) return;
      console.error('Error fetching today sessions:', err);
      setError(err.message || 'Failed to load sessions due to a server timeout or network issue.');
    } finally {
      if (fetchId !== daySessionsFetchIdRef.current) return;
      setIsLoadingDaySessions(false);
    }
  }, [studioId, fetchSessionsForMonth, categoryFilter]);

  // Re-fetch whatever's currently in view (used after create/edit/delete/copy actions).
  const refreshCurrentView = useCallback(() => {
    setCalendarRefreshKey((n) => n + 1);
    const todayStr = getTodayISTDateKey();
    const selectedDateStr = toISTDateKey(selectedDate);
    if (selectedDateStr < todayStr) {
      loadedDaysRef.current.delete(selectedDateStr);
      setPastDaySessions((prev) => {
        if (!(selectedDateStr in prev)) return prev;
        const next = { ...prev };
        delete next[selectedDateStr];
        return next;
      });
      void fetchSessionsForPastDate(selectedDateStr);
    }
    loadedMonthsRef.current.delete(displayedMonthStr);
    setMonthSessions((prev) => {
      if (!(displayedMonthStr in prev)) return prev;
      const next = { ...prev };
      delete next[displayedMonthStr];
      return next;
    });
    void fetchSessionsForMonth(displayedMonthStr);
    if (todayStr.slice(0, 7) === displayedMonthStr || todayStr === selectedDateStr) {
      void fetchTodaySessions();
    }
  }, [selectedDate, displayedMonthStr, fetchSessionsForPastDate, fetchSessionsForMonth, fetchTodaySessions]);

  // Reset all caches on studio/category change, then reload today + current month.
  useEffect(() => {
    setDaySessions([]);
    setMonthSessions({});
    setPastDaySessions({});
    loadedMonthsRef.current.clear();
    loadedDaysRef.current.clear();
  }, [studioId, categoryFilter]);

  // Proactively (and quietly) load whichever month is displayed — covers both the
  // initial month and the user paging the calendar with the arrows.
  useEffect(() => {
    if (!studioId) return;
    if (!loadedMonthsRef.current.has(displayedMonthStr)) {
      void fetchSessionsForMonth(displayedMonthStr);
    }
  }, [studioId, categoryFilter, displayedMonthStr, fetchSessionsForMonth]);

  // Today's fast path — always runs once per studio/category change. (Deliberately
  // not gated on loadedMonthsRef: the month-cache effect above marks a month as
  // loaded synchronously, before its request even resolves, which would otherwise
  // make this check false and skip the fetch — leaving isLoadingDaySessions stuck.)
  useEffect(() => {
    if (!studioId) return;
    void fetchTodaySessions();
  }, [studioId, categoryFilter, fetchTodaySessions]);

  // Fetch a specific past date's sessions the first time it's actually selected.
  useEffect(() => {
    if (!studioId) return;
    const dateStr = toISTDateKey(selectedDate);
    const todayKey = getTodayISTDateKey();
    if (dateStr < todayKey && !loadedDaysRef.current.has(dateStr)) {
      void fetchSessionsForPastDate(dateStr);
    }
  }, [studioId, selectedDate, fetchSessionsForPastDate]);

  const selectedDateStr = toISTDateKey(selectedDate);
  const todayStr = getTodayISTDateKey();
  const monthSessionsList = monthSessions[displayedMonthStr] || [];
  const isMonthSessionsLoading = monthSessions[displayedMonthStr] === undefined;
  const isSelectedDatePast = selectedDateStr < todayStr;
  const pastCachedSessions = pastDaySessions[selectedDateStr];
  const isPastDateLoading = isSelectedDatePast && pastCachedSessions === undefined;

  // Sessions shown for selected date
  const sessionsForDate = isSelectedDatePast
    ? (pastCachedSessions || [])
    : (isMonthSessionsLoading
        ? daySessions.filter((s) => toISTDateKey(new Date(s.date)) === selectedDateStr)
        : monthSessionsList.filter((s) => toISTDateKey(new Date(s.date)) === selectedDateStr));

  const sessionSearchLower = sessionSearchTerm.trim().toLowerCase();
  const filteredSessionsForDate = sessionSearchLower
    ? sessionsForDate.filter(
        (s) =>
          s.className?.toLowerCase().includes(sessionSearchLower) ||
          s.instructor?.name?.toLowerCase().includes(sessionSearchLower) ||
          s.classType?.title?.toLowerCase().includes(sessionSearchLower),
      )
    : sessionsForDate;

  const now = new Date();
  // Stat cards are scoped to the currently displayed month's loaded data (see the
  // past-date lazy-loading optimization above — a full lifetime total would require
  // fetching everything, which is exactly what this page avoids).
  const displayedMonthAllLoadedSessions = [
    ...monthSessionsList,
    ...Object.entries(pastDaySessions)
      .filter(([d]) => d.slice(0, 7) === displayedMonthStr)
      .flatMap(([, s]) => s),
  ];
  const monthTotalSessions = displayedMonthAllLoadedSessions.length;
  const monthUpcomingSessions = displayedMonthAllLoadedSessions.filter((s) => new Date(s.date) >= now).length;
  const monthPassedSessions = monthTotalSessions - monthUpcomingSessions;

  // Plain-language note for the "Sessions This Month" card: the current month's
  // count deliberately excludes already-past days (see the lazy-loading optimization).
  const currentRealMonthKey = todayStr.slice(0, 7);
  const monthTotalScopeNote = displayedMonthStr === currentRealMonthKey
    ? 'Counts today through month end'
    : displayedMonthStr > currentRealMonthKey
    ? 'Counts the full month'
    : "Counts only dates you've opened";

  const datesWithSessionsSet = new Set([
    ...monthSessionsList.map((s) => toISTDateKey(new Date(s.date))),
    ...Object.entries(pastDaySessions)
      .filter(([d, s]) => d.slice(0, 7) === displayedMonthStr && s.length > 0)
      .map(([d]) => d),
  ]);

  const hasSessions = (date: Date) => datesWithSessionsSet.has(toISTDateKey(date));

  const handleDeleteClick = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionToDelete(session);
  };

  // Custom day with dot
  const ServerDay = (props: PickersDayProps) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const show = hasSessions(day) && !outsideCurrentMonth;
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <PickersDay {...other} day={day} outsideCurrentMonth={outsideCurrentMonth} />
        {show && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: colors.primary,
            }}
          />
        )}
      </Box>
    );
  };

  const formatTime = (d: string) => formatInTimeZone(new Date(d), IST_TIMEZONE, 'hh:mm a');
  const formatDate = (d: string) => formatInTimeZone(new Date(d), IST_TIMEZONE, 'EEE, MMM d');

  const renderSessionCard = (session: Session) => {
    const isPast = new Date(session.date) < now;
    return (
      <Card
        key={session._id}
        onClick={() => navigate(`/sessions/${session._id}`)}
        sx={{
          mb: 2,
          border: `1px solid ${isPast ? colors.divider : colors.primary + '30'}`,
          opacity: isPast ? 0.85 : 1,
          transition: 'all 0.2s',
          cursor: 'pointer',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <CardContent>
          <Box display="flex" gap={2}>
            {session.image && (
              <Avatar src={session.image} variant="rounded" sx={{ width: 72, height: 72 }}>
                {session.className.charAt(0)}
              </Avatar>
            )}
            <Box flex={1}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {session.className}
                  </Typography>
                  <Chip
                    label={isPast ? 'Past' : 'Upcoming'}
                    size="small"
                    sx={{
                      mt: 0.5,
                      backgroundColor: isPast ? `${colors.textSecondary}20` : `${colors.success}20`,
                      color: isPast ? colors.textSecondary : colors.success,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20,
                    }}
                  />
                </Box>
                <Box display="flex" gap={0.5}>
                  <Chip label={`₹${session.price}`} color="primary" size="small" sx={{ fontWeight: 600 }} />
                  {canWriteSessions && (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSessionToEdit(session);
                          setEditSessionDialogOpen(true);
                        }}
                        sx={{ color: colors.primary }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDeleteClick(session, e)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                <Chip icon={<AccessTime />} label={`${session.duration} min`} size="small" variant="outlined" />
                <Chip icon={<Person />} label={`${session.slots} slots`} size="small" variant="outlined" />
                {session.classType && (
                  <Chip label={session.classType.title || session.classType.name} size="small" variant="outlined" />
                )}
                {session.sessionType === 'private' && (
                  <Chip label="Private" size="small" sx={{ backgroundColor: '#6A1B9A20', color: '#6A1B9A', fontWeight: 600 }} />
                )}
                {session.sessionType === 'duet' && (
                  <Chip label="Duet" size="small" sx={{ backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600 }} />
                )}
                {(session.category === 'wellness' || session.classType?.studioType === 'wellness') && (
                  <Chip label="Wellness" size="small" sx={{ backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600 }} />
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Event fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(session.date)} at {formatTime(session.date)}
                  </Typography>
                </Box>
                {session.instructor && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {session.instructor.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (isLoadingDaySessions) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={
        pageView === 'calendar'
          ? { backgroundColor: SCHED.canvas, borderRadius: '20px', p: { xs: '16px', md: '24px 28px' } }
          : undefined
      }
    >
      {pageView === 'calendar' ? (
        /* Calendar mode header — the Schedule design: title + studio, segmented
           switches, then the page's existing actions in the design's button style. */
        <Box
          component="header"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', mb: '20px', fontFamily: SCHED.font, color: SCHED.ink }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box component="h1" sx={{ m: 0, fontSize: '30px', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Schedule
              </Box>
              {studioDoc?.studioName && (
                <Box component="span" sx={{ fontSize: '13px', color: SCHED.muted }}>{studioDoc.studioName}</Box>
              )}
            </Box>
            <Box role="group" aria-label="Page view" sx={segSx}>
              <Box component="button" type="button" aria-pressed={false} onClick={(e: React.MouseEvent<HTMLElement>) => handlePageViewChange(e, 'list')} sx={segBtnSx(false)}>
                <ViewList sx={{ fontSize: 18 }} /> List
              </Box>
              <Box component="button" type="button" aria-pressed onClick={(e: React.MouseEvent<HTMLElement>) => handlePageViewChange(e, 'calendar')} sx={segBtnSx(true)}>
                <ViewWeek sx={{ fontSize: 18 }} /> Calendar
              </Box>
            </Box>
            {offersWellness && (
              <Box role="group" aria-label="Category" sx={segSx}>
                {(['workout', 'wellness'] as const).map((c) => (
                  <Box key={c} component="button" type="button" aria-pressed={categoryFilter === c} onClick={() => setCategoryFilter(c)} sx={segBtnSx(categoryFilter === c)}>
                    {c === 'workout' ? 'Workout' : 'Wellness'}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {canWriteSessions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Button startIcon={<Tune sx={{ fontSize: 18 }} />} endIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />} onClick={(e) => setBulkMenuAnchor(e.currentTarget)} sx={btnSx}>
                More
              </Button>
              <Button startIcon={<Add sx={{ fontSize: 18 }} />} onClick={openCreateSession} sx={btnPrimarySx}>
                Create Sessions
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          {/* Header */}
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Sessions Calendar
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage your studio's sessions
            </Typography>
          </Box>
          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
            <ToggleButtonGroup
              value={pageView}
              exclusive
              size="small"
              onChange={handlePageViewChange}
              sx={{ height: 'fit-content' }}
            >
              <ToggleButton value="list" sx={{ px: 1.5, gap: 0.75 }}>
                <ViewList fontSize="small" /> List
              </ToggleButton>
              <ToggleButton value="calendar" sx={{ px: 1.5, gap: 0.75 }}>
                <ViewWeek fontSize="small" /> Calendar
              </ToggleButton>
            </ToggleButtonGroup>
            {offersWellness && (
              <ToggleButtonGroup
                value={categoryFilter}
                exclusive
                size="small"
                onChange={(_e, val) => val && setCategoryFilter(val)}
                sx={{ height: 'fit-content' }}
              >
                <ToggleButton value="workout" sx={{ px: 2 }}>Workout</ToggleButton>
                <ToggleButton value="wellness" sx={{ px: 2 }}>Wellness</ToggleButton>
              </ToggleButtonGroup>
            )}
            {/* Copy Day, Bulk Edit / Delete and Import write the schedule
                exactly like the create form and share its permission. */}
            {canWriteSessions && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Tune />}
                  endIcon={<KeyboardArrowDown />}
                  onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
                  sx={{ height: 'fit-content' }}
                >
                  More
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreateSession}
                  sx={{ height: 'fit-content' }}
                >
                  Create Sessions
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {pageView === 'calendar' ? (
        <ScheduleCalendarView
          studioId={studioId}
          categoryFilter={categoryFilter}
          refreshKey={calendarRefreshKey}
          canWriteSessions={canWriteSessions}
          onChanged={refreshCurrentView}
          onAddSessions={canWriteSessions ? openCreateSession : undefined}
        />
      ) : (
        <>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
            <Card sx={{ background: '#ffffff', border: `1px solid ${colors.primary}`, width: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={500}>
                      Sessions This Month
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color={colors.primary}>
                      {monthTotalSessions}
                    </Typography>
                  </Box>
                  <CalendarMonth sx={{ fontSize: 40, color: colors.primary, opacity: 0.3 }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {monthTotalScopeNote}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
            <Card sx={{ background: '#ffffff', border: `1px solid ${colors.success}`, width: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={500}>
                      Upcoming This Month
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color={colors.success}>
                      {monthUpcomingSessions}
                    </Typography>
                  </Box>
                  <UpcomingOutlined sx={{ fontSize: 40, color: colors.success, opacity: 0.3 }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  From today onward
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
            <Card sx={{ background: '#ffffff', border: `1px solid ${colors.textSecondary}`, width: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={500}>
                      Passed (Viewed)
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color={colors.textSecondary}>
                      {monthPassedSessions}
                    </Typography>
                  </Box>
                  <History sx={{ fontSize: 40, color: colors.textSecondary, opacity: 0.3 }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Grows as you open past dates
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Calendar + Session list */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Paper elevation={2} sx={{ p: 1.5, height: 'fit-content' }}>
                <DateCalendar
                  value={selectedDate}
                  onChange={(d) => d && setSelectedDate(d)}
                  onMonthChange={(d) => setDisplayedMonthStr(toISTDateKey(d).slice(0, 7))}
                  slots={{ day: ServerDay }}
                  sx={{
                    width: '100%',
                    opacity: isMonthSessionsLoading ? 0.7 : 1,
                    transition: 'opacity 0.3s ease',
                    '& .MuiPickersCalendarHeader-root': { paddingLeft: 1, paddingRight: 1 },
                    '& .MuiDayCalendar-weekDayLabel': { fontSize: '0.75rem', fontWeight: 500 },
                    '& .MuiPickersDay-root': { fontSize: '0.875rem' },
                  }}
                />
              </Paper>
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Paper elevation={2} sx={{ p: 2, minHeight: 300, maxHeight: '70vh', overflow: 'auto' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </Typography>
                <Chip
                  label={`${filteredSessionsForDate.length}${sessionSearchLower && sessionsForDate.length !== filteredSessionsForDate.length ? ` of ${sessionsForDate.length}` : ''} session${filteredSessionsForDate.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ backgroundColor: `${colors.primary}15`, color: colors.primary, fontWeight: 600 }}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search by class name, instructor, or type..."
                value={sessionSearchTerm}
                onChange={(e) => setSessionSearchTerm(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.textSecondary }} />
                    </InputAdornment>
                  ),
                }}
              />

              {isPastDateLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={24} sx={{ mr: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">Loading sessions for this date…</Typography>
                </Box>
              ) : filteredSessionsForDate.length === 0 ? (
                <Alert severity="info">
                  {sessionSearchLower
                    ? 'No sessions match your search on this date.'
                    : 'No sessions scheduled for this date.'}
                </Alert>
              ) : (
                filteredSessionsForDate
                  .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                  .map(renderSessionCard)
              )}
            </Paper>
          </Grid>
        </Grid>
        </>
      )}

      {/* The More menu — every other many-sessions action in one place. */}
      <Menu anchorEl={bulkMenuAnchor} open={!!bulkMenuAnchor} onClose={() => setBulkMenuAnchor(null)}>
        <MenuItem onClick={() => startAction('copyDay')}>
          <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
          <ListItemText primary="Copy Day" secondary="Copy one day's schedule to other dates" />
        </MenuItem>
        <MenuItem onClick={() => startAction('bulkEdit')}>
          <ListItemIcon><Tune fontSize="small" /></ListItemIcon>
          <ListItemText primary="Bulk Edit / Delete" secondary="Change or remove many sessions at once" />
        </MenuItem>
        {multiLocation && (
          <MenuItem onClick={() => { setBulkMenuAnchor(null); setImportSessionsOpen(true); }}>
            <ListItemIcon><FileDownloadOutlined fontSize="small" /></ListItemIcon>
            <ListItemText primary="Import from a location" secondary="Copy another location's sessions here" />
          </MenuItem>
        )}
      </Menu>

      {/* "Which location?" — asked before a bulk action for a brand with
          several locations. */}
      <Dialog open={!!locationAsk} onClose={() => setLocationAsk(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Which location?</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <BranchSelect value={askedLocation} onChange={setAskedLocation} label="Location" minRole="manager" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationAsk(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!askedLocation}
            onClick={() => {
              const action = locationAsk;
              setLocationAsk(null);
              if (action) openAction(action, askedLocation);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Day Dialog */}
      <CopyDayDialog
        open={copyDayDialogOpen}
        onClose={() => setCopyDayDialogOpen(false)}
        targetDate={selectedDate}
        studioId={writeBranch || undefined}
        onSuccess={() => {
          setCopyDayDialogOpen(false);
          refreshCurrentView();
        }}
      />

      {/* Bulk Sessions Workspace — find a range, review, then edit or delete */}
      {actionStudioId && (
        <BulkSessionWorkspace
          open={bulkWorkspaceOpen}
          onClose={() => setBulkWorkspaceOpen(false)}
          onSuccess={refreshCurrentView}
          studioId={actionStudioId}
        />
      )}

      {/* Create Sessions — one session, a range, one location or several */}
      {accountStudioId && (
        <CreateSessionsDialog
          open={createSessionDialogOpen}
          onClose={() => setCreateSessionDialogOpen(false)}
          onSuccess={refreshCurrentView}
          studioId={accountStudioId}
          // Under "All locations" nothing is pre-ticked: the operator picks.
          preselect={isAllLocations ? [] : [accountStudioId]}
          defaultStartDate={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
        />
      )}

      {/* Import from a location — brands with 2+ locations */}
      {multiLocation && (
        <>
          <ImportSessionsDialog
            open={importSessionsOpen}
            onClose={() => { setImportSessionsOpen(false); refreshCurrentView(); }}
            onCreated={() => refreshCurrentView()}
            anchorStudioId={accountStudioId}
            preselectTarget={currentLocation[0] ?? null}
          />
        </>
      )}

      {/* Edit Session Dialog */}
      {editSessionDialogOpen && sessionToEdit && (
        <SessionFormDialog
          open={editSessionDialogOpen}
          onClose={() => {
            setEditSessionDialogOpen(false);
            setSessionToEdit(null);
          }}
          onSuccess={() => {
            setEditSessionDialogOpen(false);
            setSessionToEdit(null);
            refreshCurrentView();
          }}
          session={sessionToEdit}
          // The session's OWN location, so under "All locations" the class
          // and instructor lists are the ones of the branch it is at.
          studioId={studioIdOf((sessionToEdit as any).studio) || studioId}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteSessionDialog
        session={sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onDeleted={() => {
          setSessionToDelete(null);
          refreshCurrentView();
        }}
        onError={setError}
      />
    </Box>
  );
};

export default StudioSessionsPage;
